/**
 * RunQuestionCard — the portal's HITL surface. Where the admin playground
 * shows the raw suspension (tool name badge, suspension id, copy button),
 * a business user just needs to know: the run paused with a question, here
 * it is, answer it and the run continues by itself.
 *
 * Reuses the package's `SuspensionReplyForm` for the actual reply surface
 * (prompt / rationale / suggestions / choice / attachments — all driven by
 * the tool input), wrapped in customer-grade chrome: a warm headline, a
 * "Question N of M" pill, a friendly reply-by deadline, and honest but
 * non-technical resume-error copy.
 */
import type { PlaygroundResumeReply, ResumeError, SessionState } from '@evt/flow-stream-ui'
import { Icon, Notice, Spinner, SuspensionReplyForm } from '@evt/flow-stream-ui'
import { useEffect, useState } from 'react'
import { uploadClient, useUploadLimits } from '../api/uploads'
import { type Messages, useI18n } from '../i18n'
import { codeMessage, UPLOAD_REFUSAL_CODES } from '../i18n/errors'

export interface RunQuestionCardProps {
  state: SessionState
  maxRounds?: number
  onSubmit: (reply: PlaygroundResumeReply) => void
}

export function RunQuestionCard({ state, maxRounds, onSubmit }: RunQuestionCardProps) {
  const { t, formatBytes } = useI18n()
  // Reply files upload in parts as they are picked (ADR-0003) — no size the request body could not carry.
  const limits = useUploadLimits()
  const a = state.awaiting
  const isResuming = state.status === 'resuming'
  const remaining = useCountdown(a?.expiresAt, !!a && !isResuming)
  const expired = remaining <= 0

  if (!a) return null

  return (
    <section className="question-card" aria-label={t.run.questionCardAria}>
      <header className="question-head">
        <span className="question-glyph" aria-hidden="true">
          <Icon name="prompt" size={18} />
        </span>
        <div className="question-head-text">
          <h2 className="question-title">{isResuming ? t.run.sendingReply : t.run.inputNeeded}</h2>
          <p className="question-sub">{isResuming ? t.run.resumingSub : t.run.pausedSub}</p>
        </div>
        <div className="question-pills">
          <span className="question-pill">
            {maxRounds ? t.run.questionOfRounds(a.roundCount, maxRounds) : t.run.questionRound(a.roundCount)}
          </span>
          {Number.isFinite(remaining) && !isResuming && (
            <span className={`question-pill${expired ? ' is-expired' : remaining < 3_600_000 ? ' is-soon' : ''}`}>
              {expired ? t.run.replyWindowClosed : t.run.replyWithin(friendlyRemaining(remaining, t.run))}
            </span>
          )}
          {isResuming && (
            <span className="question-pill">
              <Spinner size={11} /> {t.run.resuming}
            </span>
          )}
        </div>
      </header>

      {state.lastResumeError && <ResumeProblem err={state.lastResumeError} />}

      {expired && !state.lastResumeError && <Notice kind="warning">{t.run.expiredNotice}</Notice>}

      <div className="question-body">
        <SuspensionReplyForm
          key={a.suspensionId}
          toolName={a.toolName}
          toolInput={a.toolInput}
          disabled={isResuming || expired}
          strings={{
            ...t.run.replyForm,
            formatBytes,
            // A failed upload's tooltip in our words: the server's message is written for an operator.
            describeUploadError: (code) => codeMessage(t, code) ?? t.run.replyForm.uploadFailed
          }}
          // A reply refused because of one of its files makes the form ask which uploads the server still holds: a
          // lapsed one fails (its retry sends the file again) instead of its chip claiming it is ready.
          uploads={{ client: uploadClient, limits, recheck: uploadRefusal(state.lastResumeError) }}
          onSubmit={onSubmit}
        />
      </div>
    </section>
  )
}

/** Non-technical resume-failure copy; keeps the honest cause in one clause. */
function ResumeProblem({ err }: { err: ResumeError }) {
  const { t } = useI18n()
  const kind = err.kind === 'in_flight' ? 'info' : 'warning'
  return <Notice kind={kind}>{resumeProblemText(t, err)}</Notice>
}

/**
 * What a failed reply tells the person. A refusal the server explained with a code the catalog words — a file to
 * attach again (`upload_expired`, 409: the question is still open), a run that moved on (`conflict`), a file fr-docs
 * could not read — says that, in the same words a refused launch uses; any other is said by its kind. Exported for the
 * test.
 */
export function resumeProblemText(t: Messages, err: ResumeError): string {
  const e = t.run.resumeError
  if (err.kind === 'other') {
    const byCode = codeMessage(t, err.issueCode)
    if (byCode) return byCode
  }
  const byKind: Record<ResumeError['kind'], string> = {
    stale: e.stale,
    not_found: e.notFound,
    in_flight: e.inFlight,
    auth: e.auth,
    network: e.network,
    other: e.other
  }
  return byKind[err.kind] ?? e.other
}

/** The resume error when the server refused the reply because of one of its files; undefined otherwise. */
export function uploadRefusal(err: ResumeError | undefined): ResumeError | undefined {
  return err?.issueCode && UPLOAD_REFUSAL_CODES.has(err.issueCode) ? err : undefined
}

function useCountdown(expiresAtIso: string | undefined, active: boolean): number {
  const [now, setNow] = useState<number>(() => Date.now())
  useEffect(() => {
    if (!active) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [active])
  if (!expiresAtIso) return Number.POSITIVE_INFINITY
  const expires = Date.parse(expiresAtIso)
  return Number.isNaN(expires) ? Number.POSITIVE_INFINITY : expires - now
}

/** "6 days", "2 days 4 hours", "3 hours", "45 minutes", "90 seconds" — localized. */
function friendlyRemaining(ms: number, m: Messages['run']): string {
  const totalMinutes = Math.floor(ms / 60_000)
  const days = Math.floor(totalMinutes / 1_440)
  const hours = Math.floor((totalMinutes % 1_440) / 60)
  const minutes = totalMinutes % 60
  if (days >= 3) return m.durDays(days)
  if (days > 0) return hours > 0 ? m.durDaysHours(days, hours) : m.durDays(days)
  if (hours > 0) return m.durHours(hours)
  if (minutes > 0) return m.durMinutes(minutes)
  return m.durSeconds(Math.max(Math.floor(ms / 1000), 0))
}
